import {
  Box,
  Heading,
  Grid,
  GridItem,
  Tag,
  Text,
  Flex,
  SimpleGrid,
  List,
  Image,
  ListItem,
  ListIcon,
  useMediaQuery,
  Icon,
  Radio,
  RadioGroup,
  Stack,
  Checkbox,
  IconButton,
  useOutsideClick,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Tooltip,
  Button,
} from '@chakra-ui/react';
import { WebMercatorViewport } from '@math.gl/web-mercator';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, Marker, Popup, Source, FullscreenControl } from 'react-map-gl';
import { useParams } from 'react-router-dom';

import {
  ICBreak,
  ICCar,
  IconBackward,
  IconBreakTime,
  IconBriefcase,
  iconBuilding,
  IconForward,
  IconPause,
  IconPlay,
  IconStop,
  ICPin,
  IconLocationPinStart,
} from 'assets/icons';
import Loader from 'components/Loader';
import Mapbox from 'components/Mapbox';
import JobPolygon from 'components/Mapbox/JobPolygon';
import JobRadius from 'components/Mapbox/JobRadius';
import { StatusMascot } from 'components/Mascot';
import { getBounds } from 'containers/WhoIsWorking/shared/helpers';
import { useFetchJobById } from 'hooks/jobs/useFetchJobById';
import { ISegment, useFetchSegmentedTimesheetById } from 'hooks/timesheet/useFetchSegmentedTimesheet';
import { formatDistanceUnit, formatTwelveHourMinuteTime, formatYearMonthDayTime } from 'utils/format';
import { isEmptyObject, isEmptyObjectWithNull, isDistanceInMiles } from 'utils/helpers';

interface ISegmentsProps {
  jobId?: any;
}

const SEGMENT_CATEGORY = {
  IDLING: 'Idling',
  ON_BREAK: 'Onbreak',
  DRIVING: 'Driving',
  ON_SITE: 'Onsite',
  CLOCK_IN: 'ClockIn',
  CLOCK_OUT: 'ClockOut',
};

interface HighLightedSegment extends ISegment {
  segmentLocation?: { latitude: number; longitude: number };
}

interface IClockInOutData {
  clockIn: { latitude?: number; longitude?: number; local_time: string; timezone?: null | string };
  clockOut: { latitude?: number; longitude?: number; local_time: string; timezone?: null | string };
}

const Segments: React.FC<ISegmentsProps> = ({ jobId }) => {
  const { t } = useTranslation(['common', 'timesheetMileage', 'segmentedTracking']);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const startEndPopupRef = useRef(null);
  const fullScreenBreakRef = useRef(null);

  const [viewport, setViewport] = useState(null);
  const [selectAllSegments, setSelectAllSegments] = useState<boolean>(false);
  const [mapView, setMapView] = useState<'street' | 'satellite'>('street');
  const [cursor, setCursor] = useState<string>('auto');
  const [isHoveredMarker, setIsHoveredMarker] = useState<boolean>(false);
  const [hoveredSegment, setHoveredSegment] = useState<HighLightedSegment | null>(null);
  const [clockInOutData, setClockInOutData] = useState<IClockInOutData | null>(null);
  const [startEndLocationPopup, setStartEndLocationPopup] = useState<ISegment | null>(null);
  const [showCoordinatesDot, setShowCoordinatesDot] = useState(false);
  const [highlightedGeoCoordinate, setHighlightedGeoCoordinate] = useState<Array<any>>([]);
  const [playState, setPlayState] = useState<boolean>(false);
  const [pauseState, setPauseState] = useState(true);
  const [activeGeoCoordinatePopup, setActiveGeoCoordinatePopup] = useState<any>(null);
  const [highlightedSegment, setHighlightedSegment] = useState<HighLightedSegment | null>(null);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  const [isLargerThan768] = useMediaQuery(['(min-width: 768px)'], { ssr: false });

  const { id } = useParams();

  const { status, data } = useFetchSegmentedTimesheetById(id!);
  const { data: jobData } = useFetchJobById(jobId);

  const categoryIconMap: { [key: string]: React.FC<React.SVGProps<SVGSVGElement>> } = {
    ClockInOut: IconLocationPinStart,
    Driving: ICCar,
    Idling: ICPin,
    Onsite: IconBriefcase,
    Onbreak: ICBreak,
  };

  const formattedGeoCoordinates: Array<any> = useMemo(() => {
    if (Array.isArray(data?.payload?.segments) && data?.payload?.segments.length) {
      const excludingClockInOutSegments = data?.payload?.segments.filter(
        segment => segment.category !== SEGMENT_CATEGORY.CLOCK_IN && segment.category !== SEGMENT_CATEGORY.CLOCK_OUT
      );
      if (excludingClockInOutSegments.length > 0) {
        const afterClockInLatitude = (excludingClockInOutSegments as any)[0]?.geolocations[0]?.latitude;
        const afterClockInLongitude = (excludingClockInOutSegments as any)[0]?.geolocations[0]?.longitude;

        const afterClockInStartTime = (excludingClockInOutSegments as any)[0]?.startTime;
        const afterClockInEndtime = (excludingClockInOutSegments as any)[0]?.endTime;

        const beforeClockOutLatitude = (excludingClockInOutSegments as any)[excludingClockInOutSegments?.length - 1]
          ?.geolocations[
          (excludingClockInOutSegments as any)[excludingClockInOutSegments?.length - 1]?.geolocations?.length - 1
        ]?.latitude;

        const beforeClockOutLongitude = (excludingClockInOutSegments as any)[excludingClockInOutSegments?.length - 1]
          ?.geolocations[
          (excludingClockInOutSegments as any)[excludingClockInOutSegments?.length - 1]?.geolocations?.length - 1
        ]?.longitude;

        const beforeClockOutStartTime = excludingClockInOutSegments[excludingClockInOutSegments?.length - 1]?.startTime;
        const beforeClockOutEndtime = excludingClockInOutSegments[excludingClockInOutSegments?.length - 1]?.endTime;

        const clockInData = data?.payload?.segments.filter(segment => segment.category === SEGMENT_CATEGORY.CLOCK_IN)[0]
          ?.location;

        const clockInLatitude = clockInData?.latitude;
        const clockInLongitude = clockInData?.longitude;

        const clockOutData = data?.payload?.segments.filter(
          segment => segment.category === SEGMENT_CATEGORY.CLOCK_OUT
        )[0]?.location;

        const clockOutLatitude = clockOutData?.latitude;
        const clockOutLongitude = clockOutData?.longitude;

        const clockOutCoords = [
          clockOutData
            ? {
                latitude: clockOutLatitude,
                longitude: clockOutLongitude,
              }
            : {},
        ];

        const generalCoordinates = [
          {
            category: SEGMENT_CATEGORY.DRIVING,
            startTime: afterClockInStartTime,
            endTime: afterClockInEndtime,
            geolocations: [
              {
                latitude: clockInLatitude,
                longitude: clockInLongitude,
              },
              {
                latitude: afterClockInLatitude,
                longitude: afterClockInLongitude,
              },
            ],
          },
          {
            category: SEGMENT_CATEGORY.DRIVING,
            startTime: beforeClockOutStartTime,
            endTime: beforeClockOutEndtime,
            geolocations: [
              ...(!isEmptyObject(clockOutCoords[0]) ? clockOutCoords : []),
              {
                latitude: beforeClockOutLatitude,
                longitude: beforeClockOutLongitude,
              },
            ],
          },
          ...excludingClockInOutSegments,
        ];

        return generalCoordinates;
      }

      return [];
    }

    return [];
  }, [data]);

  const allGeocordinates: Array<any> = useMemo(() => {
    if (Array.isArray(data?.payload?.segments) && data?.payload?.segments.length) {
      return (
        data.payload.segments
          .filter(
            segment => segment.category !== SEGMENT_CATEGORY.CLOCK_IN && segment.category !== SEGMENT_CATEGORY.CLOCK_OUT
          )
          .map(segment => {
            const allLongLat = segment?.geolocations?.length ? segment?.geolocations : [];

            return allLongLat;
          })
          .flat() || []
      );
    }

    return [];
  }, [data]);

  useOutsideClick({
    ref: startEndPopupRef,
    handler: () => closeStartEndLocationPopup(),
  });

  useEffect(() => {
    if (status === 'success') {
      if (Array.isArray(data?.payload?.segments) && data?.payload?.segments.length) {
        const clockInData = data?.payload?.segments.filter(
          segment => segment.category === SEGMENT_CATEGORY.CLOCK_IN
        )[0];
        const clockOutData = data?.payload?.segments.filter(
          segment => segment.category === SEGMENT_CATEGORY.CLOCK_OUT
        )[0];

        setClockInOutData({
          clockIn: {
            ...clockInData?.location,
            timezone: clockInData?.timezone || null,
            local_time: clockInData?.startTimeLocal || '',
          },
          clockOut: {
            ...clockOutData?.location,
            local_time: clockOutData?.endTimeLocal || '',
            timezone: clockOutData?.timezone || null,
          },
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (Array.isArray(data?.payload?.segments) && data?.payload?.segments.length) {
      const allCoordinates = allGeocordinates;

      if (selectAllSegments) {
        setHighlightedGeoCoordinate(allCoordinates);
      }

      // Check for emppty array
      if (!allCoordinates.length) return;

      // @ts-ignore
      const coordinatesBounds = getBounds(allCoordinates);

      const { longitude, latitude, zoom } = initWebMercatorViewPort(coordinatesBounds);

      setViewport((viewport: any) => ({
        ...viewport,
        longitude,
        latitude,
        zoom: zoom > 18 ? 18 : zoom,
      }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLargerThan768, allGeocordinates]);

  const initWebMercatorViewPort = (coordinatesBounds: ReturnType<typeof getBounds>) => {
    return new WebMercatorViewport({
      width: window.innerWidth,
      height: Math.max(800, window.innerHeight),
    }).fitBounds(coordinatesBounds, {
      padding: isLargerThan768
        ? { left: 300, top: 300, right: 300, bottom: 300 }
        : { left: 80, top: 80, right: 80, bottom: 80 },
      offset: [0, 0],
    });
  };

  useEffect(() => {
    const timeInterval = setInterval(() => {
      setHighlightedGeoCoordinate((coordinate: any) => {
        if (selectAllSegments)
          return [...coordinate, allGeocordinates?.length ? allGeocordinates[coordinate.length] : {}];

        if (coordinate.length === highlightedSegment?.geolocations?.length) {
          return coordinate;
        }

        return [
          ...coordinate,
          highlightedSegment?.geolocations?.length ? highlightedSegment?.geolocations[coordinate.length] : {},
        ];
      });
    }, 250);

    if (pauseState || !playState) {
      clearInterval(timeInterval);
    }

    if (!playState && !selectAllSegments) {
      setHighlightedGeoCoordinate(highlightedSegment?.geolocations?.length ? highlightedSegment?.geolocations : []);
    }

    return () => {
      clearInterval(timeInterval);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pauseState, highlightedSegment, playState]);

  const isSegmentHighlighted = (segment: ISegment) => {
    return segment.startTime === highlightedSegment?.startTime && segment.endTime === highlightedSegment?.endTime;
  };

  const isSegmentHovered = (segment: ISegment) => {
    return segment.startTime === hoveredSegment?.startTime && segment.endTime === hoveredSegment?.endTime;
  };

  const handleSegmentListClick = (segment: ISegment) => {
    setSelectAllSegments(false);
    let segmentLocation = segment?.site?.location || segment?.location;

    if (segment.category === SEGMENT_CATEGORY.DRIVING || segment.category === SEGMENT_CATEGORY.ON_BREAK) {
      segmentLocation = segment?.geolocations?.length
        ? segment?.geolocations[Math.floor(segment?.geolocations.length / 2)]
        : undefined;
    }

    if (!segmentLocation) {
      segmentLocation = segment?.geolocations?.length ? segment?.geolocations[0] : undefined;
    }

    const highlightedSegment = {
      ...segment,
      segmentLocation: segmentLocation,
    };

    if (segment?.geolocations?.length) {
      const allCoordinates = segment.geolocations || [];
      if (!allCoordinates.length) return;

      const coordinatesBounds = getBounds(allCoordinates);

      const { longitude, latitude, zoom } = initWebMercatorViewPort(coordinatesBounds);

      if (mapRef.current) {
        // @ts-ignore
        mapRef.current?.flyTo({ center: [longitude, latitude], duration: 1000, zoom: zoom > 16 ? 16 : zoom });
      }
    }

    setStartEndLocationPopup(null);
    setActiveGeoCoordinatePopup(null);
    setPauseState(false);
    setPlayState(false);
    setHighlightedGeoCoordinate(segment?.geolocations || []);
    setHighlightedSegment(highlightedSegment);
  };

  const handleSelectAllSegmentClick = () => {
    if (selectAllSegments) {
      setHighlightedSegment(null);
      setHighlightedGeoCoordinate([]);
    } else {
      setHighlightedGeoCoordinate(allGeocordinates);

      if (!allGeocordinates.length) return;

      const coordinatesBounds = getBounds(allGeocordinates);

      const { longitude, latitude, zoom } = initWebMercatorViewPort(coordinatesBounds);

      if (mapRef.current) {
        // @ts-ignore
        mapRef.current?.flyTo({ center: [longitude, latitude], duration: 1000, zoom: zoom > 16 ? 16 : zoom });
      }
    }

    setSelectAllSegments(!selectAllSegments);
  };

  const handleClockInOutClicked = (isClockIn = true): void => {
    setSelectAllSegments(false);

    const latitudeClockTime = isClockIn ? clockInOutData?.clockIn?.latitude : clockInOutData?.clockOut?.latitude;
    const longitudeClockTime = isClockIn ? clockInOutData?.clockIn?.longitude : clockInOutData?.clockOut?.longitude;

    const coordinatesBounds = getBounds(allGeocordinates);

    const { longitude, latitude } = initWebMercatorViewPort(coordinatesBounds);

    if (mapRef.current && longitude && latitude && latitudeClockTime && longitudeClockTime) {
      // @ts-ignore
      mapRef.current?.flyTo({
        center: [longitudeClockTime, latitudeClockTime],
        duration: 1000,
        zoom: 20,
      });
    }
  };

  // It is bind to interactiveLayerIds
  const handleMapMouseEnter = useCallback(() => {
    setCursor('pointer');
  }, []);

  // It is bind to interactiveLayerIds
  const handleMapMouseLeave = useCallback(() => {
    setCursor('auto');
  }, []);

  const handleMapMouseMove = useCallback(
    (e: { features: { properties: any }[] }) => {
      const coordinatesData = e.features && e.features[0]?.properties;

      // Show pop up for the breadcrumbs on mouse hover
      if (coordinatesData && coordinatesData?.dots) {
        setActiveGeoCoordinatePopup(coordinatesData);
      } else {
        setActiveGeoCoordinatePopup(null);
      }

      // Show pop up for driving lines on mouse hover
      if (coordinatesData && coordinatesData?.drivingLine) {
        if (JSON.parse(coordinatesData?.lineSegment)?.geolocations?.length) {
          if (JSON.parse(coordinatesData?.lineSegment)?.category === SEGMENT_CATEGORY.DRIVING) {
            const coordsJson = JSON.parse(coordinatesData.lineSegment).geolocations;
            const lineSegmentCoords = coordsJson[Math.floor(coordsJson.length / 2)];
            const drivingSegment = { ...JSON.parse(coordinatesData?.lineSegment), segmentLocation: lineSegmentCoords };

            setHoveredSegment(drivingSegment?.distance ? drivingSegment : null);
          }
        }
      } else {
        if (!isHoveredMarker) {
          setHoveredSegment(null);
        }
      }
    },
    [isHoveredMarker]
  );

  const handleMarkerHover = (segment: ISegment) => {
    setIsHoveredMarker(true);
    let segmentLocation = segment?.site?.location || segment?.location;

    if (segment.category === SEGMENT_CATEGORY.ON_BREAK || !(segmentLocation?.latitude && segmentLocation?.longitude)) {
      segmentLocation = segment?.geolocations?.length ? segment?.geolocations[0] : undefined;
    }

    const markerSegment = {
      ...segment,
      segmentLocation: segmentLocation,
    };

    setHoveredSegment(markerSegment);
  };

  const handlePlayButtonClick = () => {
    setPauseState(false);
    setPlayState(true);

    setHighlightedGeoCoordinate([highlightedSegment?.geolocations?.length ? highlightedSegment?.geolocations[0] : {}]);
  };

  const handlePlayFromPauseState = () => {
    setPauseState(false);
  };

  const handlePauseButtonClick = () => {
    setPauseState(true);
  };

  const handleStopButtonClick = () => {
    setPauseState(false);
    setPlayState(false);
    setHighlightedGeoCoordinate([highlightedSegment?.geolocations?.length ? highlightedSegment?.geolocations[0] : {}]);
  };

  const openStartEndLocationPopup = (segment: ISegment) => {
    setStartEndLocationPopup(segment);
  };

  const closeStartEndLocationPopup = () => {
    setStartEndLocationPopup(null);
  };

  const handleMapViewChange = (nextValue: 'street' | 'satellite') => {
    setMapView(nextValue);
  };

  const handleShowCoordinatesCheckboxClick = () => {
    setShowCoordinatesDot(showDot => !showDot);
    setActiveGeoCoordinatePopup(null);
    setPauseState(false);
    setPlayState(false);
  };

  const handleForwardButtonClick = () => {
    if (
      !highlightedSegment?.geolocations ||
      highlightedGeoCoordinate.length >= highlightedSegment?.geolocations?.length
    )
      return;

    const newCoordinates = [
      ...highlightedGeoCoordinate,
      highlightedSegment?.geolocations ? highlightedSegment?.geolocations[highlightedGeoCoordinate.length] : {},
    ];
    setHighlightedGeoCoordinate(newCoordinates);
  };

  const handleBackwardButtonClick = () => {
    if (highlightedGeoCoordinate.length <= 1) return;

    const newCoordinates = [...highlightedGeoCoordinate];
    newCoordinates.pop();
    setHighlightedGeoCoordinate(newCoordinates);
  };

  const handleMapClick = (e: any) => {
    const coordinatesData = e.features[0]?.properties;

    if (coordinatesData && coordinatesData?.dots) {
      setActiveGeoCoordinatePopup(coordinatesData);
    } else {
      setActiveGeoCoordinatePopup(null);
    }

    // Handles the driving line highlighting segment on the line click
    if (coordinatesData && coordinatesData?.drivingLine && coordinatesData?.lineSegment) {
      handleSegmentListClick(JSON.parse(coordinatesData?.lineSegment));
    }
  };

  addEventListener('fullscreenchange', () => {
    setIsFullScreen(!isFullScreen);
  });

  const getSegmentLabel = (category: string) => {
    const segmentString: string = t('timesheetMileage:segment') + ': ';

    switch (category) {
      case SEGMENT_CATEGORY.ON_BREAK:
        return segmentString + t('common:break');
      case SEGMENT_CATEGORY.ON_SITE:
        return segmentString + t('common:onsite');
      case SEGMENT_CATEGORY.IDLING:
        return segmentString + t('common:stopped');
      case SEGMENT_CATEGORY.DRIVING:
        return segmentString + t('common:driving');
      default:
        return segmentString;
    }
  };

  const getLineIds = (segments: any[]): string[] => {
    const lineIds: string[] = [];

    for (let i = 0; i < segments.length; i++) {
      const lineId = `line-${i}`;
      lineIds.push(lineId);
    }

    return lineIds;
  };

  if (status === 'loading') return <Loader />;

  if (status === 'error' || !data?.payload?.segments?.length)
    return (
      <StatusMascot
        type="error"
        title="Oops! Something went wrong."
        description={'We couldn’t load the segments data. Please refresh the page or try again later.'}
        wrapperProps={{ height: '100%' }}
      />
    );

  return (
    <Box mt="8">
      <Flex alignItems="center" justifyContent="space-between" mb="4" w={{ base: '100%', xl: '35%' }}>
        <Heading size="xs">{t('timesheetMileage:segments')}</Heading>
        <Button variant="ghost" h="fit-content" py={1} onClick={handleSelectAllSegmentClick}>
          {selectAllSegments ? t('timesheetMileage:deselectAllSegments') : t('timesheetMileage:selectAllSegments')}
        </Button>
      </Flex>
      <Grid templateColumns={{ base: '1fr', xl: '35% 1fr' }} h="fit-content" columnGap="7" rowGap="7" pb="10">
        <GridItem maxH="50vh" minHeight={{ base: '300px', xl: '400px' }} overflow="auto">
          <List
            pl="9"
            _before={{
              position: 'absolute',
              content: '""',
              top: 0,
              bottom: 0,
              left: selectAllSegments ? 2 : 2.5,
              width: selectAllSegments ? '3' : '2',
              background: selectAllSegments ? 'newOrange.1000' : 'newBlue.200',
            }}
            position="relative"
          >
            {!isEmptyObjectWithNull(clockInOutData?.clockIn) ? (
              <ListItem
                position="relative"
                _before={{
                  position: 'absolute',
                  content: 'none',
                  top: 0,
                  bottom: 0,
                  left: -7,
                  width: '3',
                  background: 'newOrange.1000',
                }}
                cursor="pointer"
                onClick={() => handleClockInOutClicked(true)}
              >
                <Box
                  position="absolute"
                  top="50%"
                  left="-34px"
                  transform="translateY(-50%)"
                  bg="white"
                  boxSize="6"
                  border="1px solid"
                  borderRadius="full"
                  borderColor="newBlue.200"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <ListIcon
                    as={categoryIconMap.ClockInOut}
                    color="green.500"
                    m="0"
                    boxSize="4"
                    sx={{ '& g': { path: { fill: 'black' } } }}
                  />
                </Box>
                <Tooltip label={t('common:clockIn')} hasArrow bg="black">
                  <Box
                    justifyContent="space-between"
                    border="0.5px solid"
                    borderColor={'transparent'}
                    borderRadius="base"
                    mb="3"
                    p="3"
                    fontWeight="semibold"
                    bg="gray.200"
                  >
                    <Flex justifyContent="space-between" textTransform="uppercase" alignItems="center">
                      <Tag
                        colorScheme="primary"
                        bg="rgba(72, 82, 175, 0.2)"
                        color="primary.500"
                        fontWeight="700"
                        lineHeight="1.5"
                        borderRadius="base"
                      >
                        {t('common:clockIn')}
                      </Tag>
                      <Flex justifyContent="flex-end" direction="column" alignItems="flex-end">
                        <Text fontWeight="bold">
                          {clockInOutData?.clockIn?.local_time
                            ? formatTwelveHourMinuteTime(clockInOutData?.clockIn?.local_time)
                            : '-'}
                        </Text>
                        {clockInOutData?.clockIn?.timezone ? (
                          <Tag bg="secondary.100" size="sm" color="secondary.600" fontSize="xs" fontWeight="semibold">
                            {clockInOutData?.clockIn?.timezone}
                          </Tag>
                        ) : null}
                      </Flex>
                    </Flex>
                  </Box>
                </Tooltip>
              </ListItem>
            ) : null}
            {Array.isArray(data?.payload?.segments) && data?.payload?.segments.length
              ? data.payload.segments
                  .filter(
                    segment =>
                      segment.category !== SEGMENT_CATEGORY.CLOCK_IN && segment.category !== SEGMENT_CATEGORY.CLOCK_OUT
                  )
                  .map((segment, index: number) => {
                    const isHighlighted = isSegmentHighlighted(segment) || selectAllSegments;

                    return (
                      <ListItem
                        key={index}
                        position="relative"
                        _before={{
                          position: 'absolute',
                          content: isHighlighted ? '""' : 'none',
                          top: 0,
                          bottom: 0,
                          left: -7,
                          width: '3',
                          background: 'newOrange.1000',
                        }}
                        cursor="pointer"
                        onClick={() => handleSegmentListClick(segment)}
                      >
                        <Box
                          position="absolute"
                          top="50%"
                          left="-34px"
                          transform="translateY(-50%)"
                          bg={isHighlighted ? 'newOrange.1000' : 'white'}
                          boxSize="6"
                          border="1px solid"
                          borderRadius="full"
                          borderColor="newBlue.200"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <ListIcon
                            as={categoryIconMap[segment.category]}
                            color="green.500"
                            m="0"
                            boxSize="3"
                            sx={{ '& path': { fill: isHighlighted ? 'white' : 'gray.500' } }}
                          />
                        </Box>
                        <Tooltip label={getSegmentLabel(segment.category)} hasArrow bg="black">
                          <Box
                            justifyContent="space-between"
                            alignItems="center"
                            border="0.5px solid"
                            borderColor={isHighlighted ? 'transparent' : 'newBlue.200'}
                            borderRadius="base"
                            mb="3"
                            p="3"
                            fontWeight="semibold"
                            bg={isHighlighted ? 'newOrange.100' : 'white'}
                          >
                            <Box textTransform="capitalize">
                              {segment.category === SEGMENT_CATEGORY.ON_BREAK && (
                                <Tag background="newGray.200" lineHeight="1.5" borderRadius="base">
                                  {segment?.site?.name || t('common:break')}
                                </Tag>
                              )}
                              {segment.category === SEGMENT_CATEGORY.ON_SITE && (
                                <Tag
                                  colorScheme="primary"
                                  bg="rgba(72, 82, 175, 0.2)"
                                  color="primary.500"
                                  fontWeight="700"
                                  lineHeight="1.5"
                                  borderRadius="base"
                                >
                                  {segment?.site?.name || t('common:onsite')}
                                </Tag>
                              )}
                              {segment.category === SEGMENT_CATEGORY.IDLING && (
                                <Tag
                                  colorScheme="primary"
                                  color="primary.500"
                                  fontWeight="700"
                                  lineHeight="1.5"
                                  borderRadius="base"
                                >
                                  {segment?.site?.name || t('common:idling')}
                                </Tag>
                              )}
                              {segment.category === SEGMENT_CATEGORY.DRIVING && (
                                <Text fontSize="sm">
                                  {isDistanceInMiles()
                                    ? segment?.distanceInMiles
                                      ? formatDistanceUnit(segment.distanceInMiles as number)
                                      : '-'
                                    : segment?.distanceInKm
                                      ? formatDistanceUnit(segment.distanceInKm as number)
                                      : '-'}
                                </Text>
                              )}
                            </Box>
                            <SimpleGrid gridTemplateColumns={'10% 1fr'}>
                              <Flex position="relative">
                                {Array.isArray(segment?.breaks) && segment.breaks.length > 0
                                  ? segment.breaks.map((breaks: any, index: number) => {
                                      return (
                                        <Tooltip
                                          bg="primary.700"
                                          label={
                                            <Box>
                                              <Text
                                                fontWeight={700}
                                                textTransform="uppercase"
                                                textAlign="center"
                                                mb={1}
                                              >
                                                {`${t('common:break')} ${breaks?.full ? '' : '(CONTINUED)'}`}
                                              </Text>
                                              <SimpleGrid columns={2} spacingX={4} spacingY={2}>
                                                <Text>
                                                  <Text as="span" mr={1}>
                                                    {t('common:name')}:
                                                  </Text>
                                                  {breaks?.name}
                                                </Text>
                                                <Text>
                                                  <Text as="span" mr={1}>
                                                    {t('common:duration')}:
                                                  </Text>
                                                  {breaks?.duration}
                                                </Text>
                                                <Text>
                                                  <Text as="span" mr={1}>
                                                    {t('common:start')}:
                                                  </Text>
                                                  {formatTwelveHourMinuteTime(breaks?.startTimeLocal)}
                                                </Text>
                                                <Text>
                                                  <Text as="span" mr={1}>
                                                    {t('common:end')}:
                                                  </Text>
                                                  {formatTwelveHourMinuteTime(breaks?.endTimeLocal)}
                                                </Text>
                                              </SimpleGrid>
                                            </Box>
                                          }
                                          hasArrow
                                          key={index}
                                        >
                                          <Box>
                                            <IconButton
                                              aria-label="Break time"
                                              as={IconBreakTime}
                                              variant="unstyled"
                                              sx={{ h: '14px', w: '14px', fill: 'gray.600' }}
                                            />
                                          </Box>
                                        </Tooltip>
                                      );
                                    })
                                  : null}
                              </Flex>
                              <Flex w="100%" justifyContent="flex-end" gridGap="2" fontSize="xs" alignItems="center">
                                <Text textTransform="uppercase">
                                  {formatTwelveHourMinuteTime(segment.startTimeLocal!)} -{' '}
                                  {formatTwelveHourMinuteTime(segment.endTimeLocal!)}
                                </Text>
                                <Tag
                                  background={isHighlighted ? 'white' : 'newBlue.100'}
                                  fontWeight="semibold"
                                  lineHeight="1.5"
                                  fontSize="xs"
                                >
                                  {segment?.durationHuman != '0' ? segment?.durationHuman : '00:00 hrs'}
                                </Tag>
                              </Flex>
                            </SimpleGrid>
                          </Box>
                        </Tooltip>
                      </ListItem>
                    );
                  })
              : null}
            {clockInOutData?.clockOut?.latitude && clockInOutData?.clockOut?.longitude ? (
              <ListItem
                position="relative"
                _before={{
                  position: 'absolute',
                  content: 'none',
                  top: 0,
                  bottom: 0,
                  left: -7,
                  width: '3',
                  background: 'newOrange.1000',
                }}
                cursor="pointer"
                onClick={() => handleClockInOutClicked(false)}
              >
                <Box
                  position="absolute"
                  top="50%"
                  left="-34px"
                  transform="translateY(-50%)"
                  bg="white"
                  boxSize="6"
                  border="1px solid"
                  borderRadius="full"
                  borderColor="newBlue.200"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <ListIcon
                    as={categoryIconMap.ClockInOut}
                    color="green.500"
                    m="0"
                    boxSize="4"
                    sx={{ '& g': { path: { fill: 'black' } } }}
                  />
                </Box>
                <Tooltip label={t('common:clockOut')} hasArrow bg="black">
                  <Box
                    justifyContent="space-between"
                    border="0.5px solid"
                    borderColor={'transparent'}
                    borderRadius="base"
                    mb="3"
                    p="3"
                    fontWeight="semibold"
                    bg="gray.200"
                  >
                    <Flex justifyContent="space-between" textTransform="uppercase" alignItems="center">
                      <Tag
                        colorScheme="primary"
                        bg="rgba(72, 82, 175, 0.2)"
                        color="primary.500"
                        fontWeight="700"
                        lineHeight="1.5"
                        borderRadius="base"
                      >
                        {t('common:clockOut')}
                      </Tag>
                      <Flex justifyContent="flex-end" direction="column" alignItems="flex-end">
                        <Text fontWeight="bold">
                          {clockInOutData?.clockOut?.local_time
                            ? formatTwelveHourMinuteTime(clockInOutData?.clockOut?.local_time)
                            : '-'}
                        </Text>
                        {clockInOutData?.clockOut?.timezone ? (
                          <Tag bg="secondary.100" size="sm" color="secondary.600" fontSize="xs" fontWeight="semibold">
                            {clockInOutData?.clockOut?.timezone}
                          </Tag>
                        ) : null}
                      </Flex>
                    </Flex>
                  </Box>
                </Tooltip>
              </ListItem>
            ) : null}
          </List>
        </GridItem>
        <GridItem height="50vh" minHeight="400px">
          {viewport ? (
            <Mapbox
              initialViewState={viewport}
              style={{ width: '100%', height: '100%' }}
              cursor={cursor}
              mapStyle={
                mapView === 'street'
                  ? 'mapbox://styles/mapbox/streets-v11'
                  : 'mapbox://styles/mapbox/satellite-streets-v9'
              }
              interactiveLayerIds={
                showCoordinatesDot
                  ? [...getLineIds(formattedGeoCoordinates), 'dots']
                  : [...getLineIds(formattedGeoCoordinates)]
              }
              onMouseEnter={handleMapMouseEnter}
              onMouseLeave={handleMapMouseLeave}
              onMouseMove={handleMapMouseMove}
              ref={mapRef}
              onClick={handleMapClick}
            >
              <FullscreenControl />
              {/* map style */}
              <RadioGroup
                onChange={handleMapViewChange}
                value={mapView}
                position="absolute"
                top="10px"
                left="10px"
                p="2"
                bg="white"
                colorScheme="primary"
                fontWeight="semibold"
                borderRadius="lg"
              >
                <Stack direction="row" cursor="pointer">
                  <Radio size="sm" value="street" mr="2">
                    {t('common:street')}
                  </Radio>
                  <Radio size="sm" value="satellite">
                    {t('common:satellite')}
                  </Radio>
                </Stack>
              </RadioGroup>
              {highlightedSegment || selectAllSegments ? (
                <Box
                  bg="white"
                  py="2"
                  px="3"
                  position="absolute"
                  top="55px"
                  left="10px"
                  fontSize="sm"
                  borderRadius="base"
                >
                  <Checkbox
                    isChecked={showCoordinatesDot}
                    size="sm"
                    onChange={handleShowCoordinatesCheckboxClick}
                    colorScheme="primary"
                  >
                    {t('common:showBreadcrumbs')}
                  </Checkbox>
                </Box>
              ) : null}
              {isFullScreen ? (
                <Box maxW={{ base: '42%', xs: '100%' }} position="absolute" top="15%" left="10px" bg="white">
                  <Accordion defaultIndex={[0]} allowMultiple>
                    <AccordionItem>
                      <Heading size="xs" bg="primary.500" borderRadius="base" color="white">
                        <AccordionButton display="flex" w="full" justifyContent="space-between">
                          {t('timesheetMileage:segments')}

                          <AccordionIcon />
                        </AccordionButton>
                      </Heading>
                      <AccordionPanel>
                        <Button variant="secondary" h="fit-content" py={1} mb={1} onClick={handleSelectAllSegmentClick}>
                          {t('timesheetMileage:selectAllSegments')}
                        </Button>
                        <GridItem maxH="447px" overflow="auto">
                          <List
                            pl="9"
                            _before={{
                              position: 'absolute',
                              content: '""',
                              top: 0,
                              bottom: 0,
                              left: selectAllSegments ? 2 : 2.5,
                              width: selectAllSegments ? '3' : '2',
                              background: selectAllSegments ? 'newOrange.1000' : 'newBlue.200',
                            }}
                            position="relative"
                            bg="white"
                          >
                            {!isEmptyObjectWithNull(clockInOutData?.clockIn) ? (
                              <ListItem
                                position="relative"
                                _before={{
                                  position: 'absolute',
                                  content: 'none',
                                  top: 0,
                                  bottom: 0,
                                  left: -7,
                                  width: '3',
                                  background: 'newOrange.1000',
                                }}
                                cursor="pointer"
                                onClick={() => handleClockInOutClicked(true)}
                              >
                                <Box
                                  position="absolute"
                                  top="50%"
                                  left="-34px"
                                  transform="translateY(-50%)"
                                  bg="white"
                                  boxSize="6"
                                  border="1px solid"
                                  borderRadius="full"
                                  borderColor="newBlue.200"
                                  display="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  <ListIcon
                                    as={categoryIconMap.ClockInOut}
                                    color="green.500"
                                    m="0"
                                    boxSize="4"
                                    sx={{ '& g': { path: { fill: 'black' } } }}
                                  />
                                </Box>
                                <Tooltip label={t('common:clockIn')} hasArrow bg="black">
                                  <Box
                                    justifyContent="space-between"
                                    border="0.5px solid"
                                    borderColor={'transparent'}
                                    borderRadius="base"
                                    mb="3"
                                    p="3"
                                    fontWeight="semibold"
                                    bg="gray.200"
                                  >
                                    <Flex justifyContent="space-between" textTransform="uppercase" alignItems="center">
                                      <Tag
                                        colorScheme="primary"
                                        bg="rgba(72, 82, 175, 0.2)"
                                        color="primary.500"
                                        fontWeight="700"
                                        lineHeight="1.5"
                                        borderRadius="base"
                                      >
                                        {t('common:clockIn')}
                                      </Tag>
                                      <Flex justifyContent="flex-end" direction="column" alignItems="flex-end">
                                        <Text fontWeight="bold">
                                          {clockInOutData?.clockIn?.local_time
                                            ? formatTwelveHourMinuteTime(clockInOutData?.clockIn?.local_time)
                                            : '-'}
                                        </Text>
                                        {clockInOutData?.clockIn?.timezone ? (
                                          <Tag
                                            bg="secondary.100"
                                            size="sm"
                                            color="secondary.600"
                                            fontSize="xs"
                                            fontWeight="semibold"
                                          >
                                            {clockInOutData?.clockIn?.timezone}
                                          </Tag>
                                        ) : null}
                                      </Flex>
                                    </Flex>
                                  </Box>
                                </Tooltip>
                              </ListItem>
                            ) : null}
                            {Array.isArray(data?.payload?.segments) && data?.payload?.segments.length
                              ? data.payload.segments
                                  .filter(
                                    segment =>
                                      segment.category !== SEGMENT_CATEGORY.CLOCK_IN &&
                                      segment.category !== SEGMENT_CATEGORY.CLOCK_OUT
                                  )
                                  .map((segment, index: number) => {
                                    const isHighlighted = isSegmentHighlighted(segment) || selectAllSegments;
                                    return (
                                      <ListItem
                                        key={index}
                                        position="relative"
                                        _before={{
                                          position: 'absolute',
                                          content: isHighlighted ? '""' : 'none',
                                          top: 0,
                                          bottom: 0,
                                          left: -7,
                                          width: '3',
                                          background: 'newOrange.1000',
                                        }}
                                        cursor="pointer"
                                        onClick={() => handleSegmentListClick(segment)}
                                      >
                                        <Box
                                          position="absolute"
                                          top="50%"
                                          left="-34px"
                                          transform="translateY(-50%)"
                                          bg={isHighlighted ? 'newOrange.1000' : 'white'}
                                          boxSize="6"
                                          border="1px solid"
                                          borderRadius="full"
                                          borderColor="newBlue.200"
                                          display="flex"
                                          justifyContent="center"
                                          alignItems="center"
                                        >
                                          <ListIcon
                                            as={categoryIconMap[segment.category]}
                                            color="green.500"
                                            m="0"
                                            boxSize="3"
                                            sx={{ '& path': { fill: isHighlighted ? 'white' : 'gray.500' } }}
                                          />
                                        </Box>
                                        <Box
                                          title={getSegmentLabel(segment.category)}
                                          justifyContent="space-between"
                                          alignItems="center"
                                          border="0.5px solid"
                                          borderColor={isHighlighted ? 'transparent' : 'newBlue.200'}
                                          borderRadius="base"
                                          mb="3"
                                          p="3"
                                          minW="400px"
                                          fontWeight="semibold"
                                          bg={isHighlighted ? 'newOrange.100' : 'white'}
                                        >
                                          <Box textTransform="capitalize">
                                            {segment.category === SEGMENT_CATEGORY.ON_BREAK && (
                                              <Tag background="newGray.200" lineHeight="1.5" borderRadius="base">
                                                {segment?.site?.name || t('common:break')}
                                              </Tag>
                                            )}
                                            {segment.category === SEGMENT_CATEGORY.ON_SITE && (
                                              <Tag
                                                colorScheme="primary"
                                                color="primary.500"
                                                fontWeight="700"
                                                lineHeight="1.5"
                                                borderRadius="base"
                                              >
                                                {segment?.site?.name || t('common:onsite')}
                                              </Tag>
                                            )}
                                            {segment.category === SEGMENT_CATEGORY.IDLING && (
                                              <Tag
                                                colorScheme="primary"
                                                color="primary.500"
                                                fontWeight="700"
                                                lineHeight="1.5"
                                                borderRadius="base"
                                              >
                                                {segment?.site?.name || t('common:idling')}
                                              </Tag>
                                            )}
                                            {segment.category === SEGMENT_CATEGORY.DRIVING && (
                                              <Text fontSize="sm">
                                                {isDistanceInMiles()
                                                  ? segment?.distanceInMiles
                                                    ? formatDistanceUnit(segment.distanceInMiles as number)
                                                    : '-'
                                                  : segment?.distanceInKm
                                                    ? formatDistanceUnit(segment.distanceInKm as number)
                                                    : '-'}
                                              </Text>
                                            )}
                                          </Box>
                                          <SimpleGrid gridTemplateColumns={'25% 1fr'}>
                                            <Box width="25%" position="relative">
                                              {Array.isArray(segment?.breaks) && segment.breaks.length > 0
                                                ? segment.breaks.map((breaks: any, index: number) => {
                                                    return (
                                                      <Tooltip
                                                        bg="primary.700"
                                                        label={
                                                          <Box>
                                                            <Text
                                                              fontWeight={700}
                                                              textTransform="uppercase"
                                                              textAlign="center"
                                                              mb={1}
                                                            >
                                                              {`${t('common:break')} ${
                                                                breaks?.full ? '' : '(CONTINUED)'
                                                              }`}
                                                            </Text>
                                                            <SimpleGrid columns={2} spacingX={4} spacingY={2}>
                                                              <Text>
                                                                <Text as="span" mr={1}>
                                                                  {t('common:name')}:
                                                                </Text>
                                                                {breaks?.name}
                                                              </Text>
                                                              <Text>
                                                                <Text as="span" mr={1}>
                                                                  {t('common:duration')}:
                                                                </Text>
                                                                {breaks?.duration}
                                                              </Text>
                                                              <Text>
                                                                <Text as="span" mr={1}>
                                                                  {t('common:start')}:
                                                                </Text>
                                                                {formatTwelveHourMinuteTime(breaks?.startTimeLocal)}
                                                              </Text>
                                                              <Text>
                                                                <Text as="span" mr={1}>
                                                                  {t('common:end')}:
                                                                </Text>
                                                                {formatTwelveHourMinuteTime(breaks?.endTimeLocal)}
                                                              </Text>
                                                            </SimpleGrid>
                                                          </Box>
                                                        }
                                                        hasArrow
                                                        portalProps={{ containerRef: fullScreenBreakRef }}
                                                        key={index}
                                                      >
                                                        <Box ref={fullScreenBreakRef}>
                                                          <IconButton
                                                            aria-label="Break time"
                                                            as={IconBreakTime}
                                                            variant="unstyled"
                                                            sx={{ h: '14px', w: '14px', fill: 'gray.600' }}
                                                          />
                                                        </Box>
                                                      </Tooltip>
                                                    );
                                                  })
                                                : null}
                                            </Box>
                                            <Flex
                                              w="100%"
                                              justifyContent="flex-end"
                                              gridGap="2"
                                              fontSize="xs"
                                              alignItems="center"
                                            >
                                              <Text textTransform="uppercase">
                                                {formatTwelveHourMinuteTime(segment.startTimeLocal!)} -{' '}
                                                {formatTwelveHourMinuteTime(segment.endTimeLocal!)}
                                              </Text>
                                              <Tag
                                                background={isHighlighted ? 'white' : 'newBlue.100'}
                                                fontWeight="semibold"
                                                lineHeight="1.5"
                                                fontSize="xs"
                                              >
                                                {segment?.durationHuman != '0' ? segment?.durationHuman : '00:00 hrs'}
                                              </Tag>
                                            </Flex>
                                          </SimpleGrid>
                                        </Box>
                                      </ListItem>
                                    );
                                  })
                              : null}
                            {!isEmptyObjectWithNull(clockInOutData?.clockOut) ? (
                              <ListItem
                                position="relative"
                                _before={{
                                  position: 'absolute',
                                  content: 'none',
                                  top: 0,
                                  bottom: 0,
                                  left: -7,
                                  width: '3',
                                  background: 'newOrange.1000',
                                }}
                                cursor="pointer"
                                onClick={() => handleClockInOutClicked(false)}
                              >
                                <Box
                                  position="absolute"
                                  top="50%"
                                  left="-34px"
                                  transform="translateY(-50%)"
                                  bg="white"
                                  boxSize="6"
                                  border="1px solid"
                                  borderRadius="full"
                                  borderColor="newBlue.200"
                                  display="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  <ListIcon
                                    as={categoryIconMap.ClockInOut}
                                    color="green.500"
                                    m="0"
                                    boxSize="4"
                                    sx={{ '& g': { path: { fill: 'black' } } }}
                                  />
                                </Box>
                                <Tooltip label={t('common:clockOut')} hasArrow bg="black">
                                  <Box
                                    justifyContent="space-between"
                                    border="0.5px solid"
                                    borderColor={'transparent'}
                                    borderRadius="base"
                                    mb="3"
                                    p="3"
                                    fontWeight="semibold"
                                    bg="gray.200"
                                  >
                                    <Flex justifyContent="space-between" textTransform="uppercase" alignItems="center">
                                      <Tag
                                        colorScheme="primary"
                                        bg="rgba(72, 82, 175, 0.2)"
                                        color="primary.500"
                                        fontWeight="700"
                                        lineHeight="1.5"
                                        borderRadius="base"
                                      >
                                        {t('common:clockOut')}
                                      </Tag>
                                      <Flex justifyContent="flex-end" direction="column" alignItems="flex-end">
                                        <Text fontWeight="bold">
                                          {clockInOutData?.clockOut?.local_time
                                            ? formatTwelveHourMinuteTime(clockInOutData?.clockOut?.local_time)
                                            : '-'}
                                        </Text>
                                        {clockInOutData?.clockOut?.timezone ? (
                                          <Tag
                                            bg="secondary.100"
                                            size="sm"
                                            color="secondary.600"
                                            fontSize="xs"
                                            fontWeight="semibold"
                                          >
                                            {clockInOutData?.clockOut?.timezone}
                                          </Tag>
                                        ) : null}
                                      </Flex>
                                    </Flex>
                                  </Box>
                                </Tooltip>
                              </ListItem>
                            ) : null}
                          </List>
                        </GridItem>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Box>
              ) : null}
              {showCoordinatesDot && (highlightedSegment || selectAllSegments) ? (
                <Box
                  position="absolute"
                  top="80px"
                  left="10px"
                  ml="auto"
                  mt="3"
                  p="2"
                  pl="0"
                  bg="transparent"
                  borderRadius="base"
                >
                  {playState ? (
                    <>
                      <IconButton
                        onClick={handleStopButtonClick}
                        mr="2"
                        aria-label="play icon"
                        bg="secondary.500"
                        size="sm"
                      >
                        <Icon as={IconStop} />
                      </IconButton>
                      {pauseState && (
                        <IconButton
                          onClick={handleBackwardButtonClick}
                          mr="2"
                          aria-label="back icon"
                          bg="secondary.500"
                          size="sm"
                          isDisabled={highlightedGeoCoordinate.length === 1}
                        >
                          <Icon as={IconBackward} />
                        </IconButton>
                      )}
                      {pauseState ? (
                        <IconButton
                          onClick={handlePlayFromPauseState}
                          mr="2"
                          aria-label="play icon"
                          bg="secondary.500"
                          size="sm"
                        >
                          <Icon as={IconPlay} />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={handlePauseButtonClick}
                          mr="2"
                          aria-label="play icon"
                          bg="secondary.500"
                          size="sm"
                        >
                          <Icon as={IconPause} />
                        </IconButton>
                      )}
                      {pauseState && (
                        <IconButton
                          onClick={handleForwardButtonClick}
                          mr="2"
                          aria-label="forward icon"
                          bg="secondary.500"
                          size="sm"
                          isDisabled={highlightedGeoCoordinate.length === allGeocordinates.length}
                        >
                          <Icon as={IconForward} />
                        </IconButton>
                      )}
                    </>
                  ) : (
                    <IconButton
                      onClick={handlePlayButtonClick}
                      mr="2"
                      aria-label="play icon"
                      bg="secondary.500"
                      size="sm"
                    >
                      <Icon as={IconPlay} />
                    </IconButton>
                  )}
                </Box>
              ) : null}
              {showCoordinatesDot ? (
                <Source
                  id="dots"
                  type="geojson"
                  data={{
                    type: 'FeatureCollection',
                    // @ts-ignore
                    features: highlightedGeoCoordinate.map((geoCoordinate: any) => {
                      return {
                        type: 'Feature',
                        properties: { ...geoCoordinate, dots: true },
                        geometry: { type: 'Point', coordinates: [geoCoordinate?.longitude, geoCoordinate?.latitude] },
                      };
                    }),
                  }}
                >
                  <Layer
                    id="dots"
                    type="circle"
                    paint={{
                      'circle-color': '#FF6720',
                      'circle-radius': 6,
                      'circle-stroke-width': 2,
                      'circle-stroke-color': '#ffffff',
                    }}
                  />
                </Source>
              ) : null}
              {/* Job Marker */}
              {jobData?.payload?.longitude && jobData?.payload?.latitude ? (
                <Marker longitude={jobData?.payload?.longitude} latitude={jobData?.payload?.latitude} offset={[0, 0]}>
                  <Image
                    boxSize="32px"
                    bg="white"
                    objectFit="cover"
                    borderRadius="50%"
                    src={iconBuilding}
                    alt="building icon"
                    p="1"
                  />
                </Marker>
              ) : null}

              {/* Headquarter Job Radius */}
              {jobData?.payload?.requireGeofence &&
              !jobData?.payload.custom_geofence &&
              jobData?.payload?.longitude &&
              jobData?.payload?.latitude &&
              jobData?.payload?.radius ? (
                <JobRadius
                  sourceId="geofence"
                  latitude={jobData?.payload?.latitude}
                  longitude={jobData?.payload?.longitude}
                  radius={jobData?.site?.radius}
                />
              ) : null}

              {/* Headquarter Job polygon */}
              {jobData?.payload?.requireGeofence &&
              jobData?.payload?.custom_geofence &&
              Array.isArray(jobData?.payload?.geofence_coordinates) ? (
                <JobPolygon sourceId="headquarter-polygon" coordinates={jobData?.payload?.geofence_coordinates || []} />
              ) : null}

              {/*Start and End Location Marker */}
              {data?.payload?.segments
                .filter(
                  segment =>
                    segment.category === SEGMENT_CATEGORY.CLOCK_IN || segment.category === SEGMENT_CATEGORY.CLOCK_OUT
                )
                .map(segment => {
                  if (segment?.location?.latitude && segment?.location.longitude) {
                    return (
                      <Marker
                        key={segment.location.latitude + segment.location.longitude}
                        latitude={segment?.location?.latitude}
                        longitude={segment?.location?.longitude}
                        offset={[0, 0]}
                      >
                        <Icon
                          as={IconLocationPinStart}
                          boxSize="10"
                          onMouseEnter={() => openStartEndLocationPopup(segment)}
                          onMouseLeave={() => {
                            setStartEndLocationPopup(null);
                          }}
                          sx={{
                            path: {
                              '&:nth-of-type(3)': {
                                fill: segment.category === SEGMENT_CATEGORY.CLOCK_IN ? 'success.500' : 'newRed.1000',
                              },
                            },
                          }}
                        />
                      </Marker>
                    );
                  }
                })}

              {/* Idling, Break, Onsite Marker */}
              {data?.payload?.segments
                .filter(
                  segment =>
                    segment.category === SEGMENT_CATEGORY.IDLING ||
                    segment.category === SEGMENT_CATEGORY.ON_BREAK ||
                    segment.category === SEGMENT_CATEGORY.ON_SITE
                )
                .map((segment, index) => {
                  let segmentLocation = segment?.site?.location;

                  if (
                    segment.category === SEGMENT_CATEGORY.ON_BREAK ||
                    !segmentLocation ||
                    segmentLocation!.longitude === undefined ||
                    segmentLocation!.latitude === undefined
                  ) {
                    segmentLocation = segment?.geolocations?.length ? segment?.geolocations[0] : undefined;
                  }

                  if (segmentLocation) {
                    return (
                      <>
                        <Marker
                          key={'marker' + index}
                          latitude={segmentLocation.latitude}
                          longitude={segmentLocation.longitude}
                          offset={[0, 0]}
                        >
                          <Box
                            bg={isSegmentHighlighted(segment) ? 'newBlue.500' : 'white'}
                            borderRadius="full"
                            border="1px solid"
                            borderColor="newBlue.500"
                            bgRepeat="no-repeat"
                            p="1"
                            boxSize="32px"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            cursor="pointer"
                            onMouseEnter={() => handleMarkerHover(segment)}
                            onMouseLeave={() => {
                              setHoveredSegment(null);
                              setIsHoveredMarker(false);
                            }}
                          >
                            <Icon
                              as={categoryIconMap[segment.category]}
                              boxSize="4"
                              sx={{ '& path': { fill: isSegmentHighlighted(segment) ? 'white' : 'newBlue.500' } }}
                            />
                          </Box>
                        </Marker>

                        {!segment?.site?.has_custom_geofence && segment?.site?.radius ? (
                          <JobRadius
                            sourceId="geofence"
                            latitude={segmentLocation?.latitude}
                            longitude={segmentLocation?.longitude}
                            radius={segment?.site?.radius}
                          />
                        ) : null}

                        {segment?.site?.has_custom_geofence &&
                        Array.isArray(segment?.site?.geofence_coordinates) &&
                        segment?.site?.geofence_coordinates.length ? (
                          <JobPolygon
                            sourceId={`polygon-${segment?.site?.name}`}
                            fillId={`fill-${segment?.site?.name}`}
                            lineId={`outline-${segment?.site?.name}`}
                            coordinates={segment?.site?.geofence_coordinates || []}
                          />
                        ) : null}
                      </>
                    );
                  }
                })}

              {/* Driving line */}
              {formattedGeoCoordinates.map((segment: any, index: number) => {
                return (
                  <Source
                    key={index}
                    id={`paths-${index}`}
                    type="geojson"
                    data={{
                      type: 'Feature',
                      properties: { drivingLine: true, lineSegment: segment },
                      geometry: {
                        type: 'LineString',
                        coordinates: segment.geolocations.map((location: any) => [
                          location.longitude,
                          location.latitude,
                        ]),
                      },
                    }}
                  >
                    <Layer
                      id={`line-${index}`}
                      type="line"
                      layout={{
                        'line-join': 'round',
                        'line-cap': 'round',
                      }}
                      paint={{
                        'line-color': isSegmentHighlighted(segment) || selectAllSegments ? '#2A2F5A' : '#ff4d00',
                        'line-width':
                          isSegmentHighlighted(segment) || selectAllSegments || isSegmentHovered(segment) ? 4 : 3,
                        'line-dasharray':
                          isSegmentHighlighted(segment) || selectAllSegments || isSegmentHovered(segment)
                            ? []
                            : [0, 2, 4],
                      }}
                    />
                  </Source>
                );
              })}
              {/* End Popup */}
              {startEndLocationPopup && (
                <Popup
                  longitude={startEndLocationPopup?.location?.longitude as number}
                  latitude={startEndLocationPopup?.location?.latitude as number}
                  closeButton={false}
                  closeOnClick={false}
                  anchor="top"
                  offset={[0, 20] as [number, number]}
                  // className="mapbox-popup-wrapper"
                >
                  <Box width="125px" ref={startEndPopupRef}>
                    {/* <Icon
                    as={IconClose}
                    boxSize="2"
                    position="absolute"
                    top="2"
                    right="2"
                    onClick={closeStartEndLocationPopup}
                    cursor="pointer"
                  /> */}
                    <Text>
                      {startEndLocationPopup.category === SEGMENT_CATEGORY.CLOCK_IN &&
                      startEndLocationPopup?.startTimeLocal
                        ? formatTwelveHourMinuteTime(startEndLocationPopup?.startTimeLocal)
                        : ''}
                      {startEndLocationPopup.category === SEGMENT_CATEGORY.CLOCK_OUT &&
                      startEndLocationPopup?.endTimeLocal
                        ? formatTwelveHourMinuteTime(startEndLocationPopup?.endTimeLocal)
                        : ''}
                    </Text>
                    <Text fontWeight="bold">
                      {startEndLocationPopup.category === SEGMENT_CATEGORY.CLOCK_IN
                        ? t('common:clockIn')
                        : t('common:clockOut')}
                    </Text>
                  </Box>
                </Popup>
              )}
              {/* Highlighted Segment job Radius */}
              {highlightedSegment?.category === SEGMENT_CATEGORY.IDLING &&
              highlightedSegment?.site?.location &&
              highlightedSegment?.site?.radius ? (
                <JobRadius
                  sourceId="highlighted-geofence"
                  circleId={`radius-${highlightedSegment?.startTimeLocal}`}
                  latitude={highlightedSegment?.site?.location?.latitude}
                  longitude={highlightedSegment?.site?.location?.longitude}
                  radius={highlightedSegment?.site?.radius}
                />
              ) : null}
              {/* geoCoordinate point popup / Breadcrumbs */}
              {activeGeoCoordinatePopup && (
                <Popup
                  latitude={activeGeoCoordinatePopup?.latitude}
                  longitude={activeGeoCoordinatePopup?.longitude}
                  closeButton={false}
                  closeOnClick={false}
                  anchor="top"
                  className="mapbox-popup-wrapper"
                >
                  <Box mt="1">
                    <Text>
                      {activeGeoCoordinatePopup?.location_time
                        ? formatYearMonthDayTime(activeGeoCoordinatePopup?.location_time ?? null)
                        : '- '}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {t('common:speed')}:{' '}
                      <Text as="span" fontWeight="semibold">
                        {!isNaN(activeGeoCoordinatePopup?.speed)
                          ? formatDistanceUnit(activeGeoCoordinatePopup?.speed)
                              .replace('miles', 'mph')
                              .replace('km', 'k/h')
                          : '- '}
                      </Text>
                    </Text>
                  </Box>
                </Popup>
              )}
              {/* Hovered Popup */}
              {hoveredSegment ? (
                <Popup
                  longitude={hoveredSegment?.segmentLocation?.longitude as number}
                  latitude={hoveredSegment?.segmentLocation?.latitude as number}
                  closeButton={false}
                  closeOnClick={false}
                  offset={[0, 10] as [number, number]}
                  anchor="top"
                  style={{
                    boxShadow: '0px 4px 44px rgba(42, 47, 90, 0.07)',
                  }}
                >
                  <Box ref={popupRef}>
                    {(hoveredSegment.category === SEGMENT_CATEGORY.ON_BREAK ||
                      hoveredSegment.category === SEGMENT_CATEGORY.IDLING ||
                      hoveredSegment.category === SEGMENT_CATEGORY.ON_SITE) && (
                      <Box width="220px" mt="1">
                        <Flex justifyContent="space-between">
                          <Text>
                            {hoveredSegment?.startTimeLocal && hoveredSegment?.endTimeLocal
                              ? `${formatTwelveHourMinuteTime(
                                  hoveredSegment.startTimeLocal
                                )} - ${formatTwelveHourMinuteTime(hoveredSegment.endTimeLocal)}`
                              : '-'}
                          </Text>
                          <Text>{hoveredSegment?.durationHuman || ''}</Text>
                        </Flex>
                        <Text fontWeight="bold">{hoveredSegment?.site?.name || ''}</Text>
                      </Box>
                    )}
                    {hoveredSegment.category === SEGMENT_CATEGORY.DRIVING && (
                      <Box width="220px">
                        <Flex justifyContent="space-between">
                          <Text color="black" fontWeight={400}>
                            {hoveredSegment?.startTimeLocal && hoveredSegment?.endTimeLocal
                              ? `${formatTwelveHourMinuteTime(
                                  hoveredSegment.startTimeLocal
                                )} - ${formatTwelveHourMinuteTime(hoveredSegment.endTimeLocal)}`
                              : '-'}
                          </Text>
                          <Text>{hoveredSegment?.durationHuman || ''}</Text>
                        </Flex>
                        <Flex alignItems="center">
                          <Icon
                            as={ICCar}
                            mr="1"
                            sx={{
                              '& path': { fill: 'secondary.500' },
                            }}
                          />
                          <Text fontWeight="bold" textTransform="capitalize">
                            {isDistanceInMiles()
                              ? hoveredSegment?.distanceInMiles
                                ? formatDistanceUnit(hoveredSegment.distanceInMiles as number)
                                : '-'
                              : hoveredSegment?.distanceInKm
                                ? formatDistanceUnit(hoveredSegment.distanceInKm as number)
                                : '-'}
                          </Text>
                        </Flex>
                      </Box>
                    )}
                  </Box>
                </Popup>
              ) : null}
            </Mapbox>
          ) : null}
        </GridItem>
      </Grid>
      {/* <Text fontStyle="italicize" fontWeight="bold" textAlign="center" textTransform="uppercase">
        {t('segmentedTracking:clockInOutDisclaimer')}
      </Text> */}
    </Box>
  );
};

export default Segments;
